import { useMemo, useState } from "react";
import { Alert, Modal, RadioPresets, Typography, type ColumnDef } from "@/deps/ui-library";
import {
  useApplyChartTemplate,
  useChartTemplate,
  useChartTemplates,
  useReplacePreview,
} from "@/api/queries";
import type { CoaTemplate, CoaTemplateAccount } from "@/api/types";
import { useSessionIfSignedIn } from "@/app/session";
import { useToast } from "@/app/toasts";
import {
  firstSentence,
  referenceKindName,
  regionName,
  statementLineName,
  templateFor,
} from "@/lib/chart";
import { ClassTables } from "./ClassTables";
import { refusalMessage } from "./refusal";

/** The set onboarding has chosen so far, which nothing has written yet. */
export interface ChosenSet {
  template: CoaTemplate;
  /** Codes dropped from it with ×. */
  dropped: string[];
}

type Props = {
  open: boolean;
  onClose: () => void;
} & (
  | {
      /** Onboarding: choosing writes nothing; the wizard applies the set when the step is saved. */
      mode: "onboarding";
      current: ChosenSet | null;
      onChoose: (template: CoaTemplate) => void;
    }
  | {
      /** Settings: before the first posting, applying replaces every account but the required and bank ones. */
      mode: "settings";
    }
);

const PREVIEW: Array<ColumnDef<CoaTemplateAccount>> = [
  { id: "code", header: "Code", size: 72, cell: ({ row }) => row.original.code },
  { id: "name", header: "Account", cell: ({ row }) => row.original.name },
  {
    id: "line",
    header: "Statement line",
    size: 220,
    cell: ({ row }) => (
      <span className="text-muted-foreground">{statementLineName(row.original.statementLine)}</span>
    ),
  },
];

/**
 * Add suggested accounts (handoff §5.2, §5.4, §6.7–6.8): the two supplied charts as cards, a note that
 * industry add-ons come later, the chosen chart's full preview by class, and what applying it replaces.
 * One picker for onboarding and Settings.
 */
export function TemplatePicker(props: Props) {
  const { open, onClose } = props;
  const templates = useChartTemplates();
  const country = useSessionIfSignedIn()?.session.organization.country;
  const current = props.mode === "onboarding" ? props.current : null;
  const [picked, setPicked] = useState<string | null>(null);
  const list = useMemo(() => templates.data ?? [], [templates.data]);
  const chosen =
    list.find((t) => t.code === picked) ??
    list.find((t) => t.code === current?.template.code) ??
    templateFor(list, country);
  const detail = useChartTemplate(chosen?.code ?? null, chosen?.version ?? null);
  const preview = useReplacePreview(props.mode === "settings" && open);
  const apply = useApplyChartTemplate();
  const toast = useToast();

  const count = chosen?.accountCount ?? 0;
  const kept = current ? current.template.accountCount - current.dropped.length : 0;
  const switching = !!current && !!chosen && current.template.code !== chosen.code;
  const replaceable = props.mode === "settings" ? (preview.data?.replaceable ?? 0) : 0;
  const blocking = props.mode === "settings" ? (preview.data?.blocking ?? []) : [];
  const ledgerHasPostings = props.mode === "settings" && preview.data?.ledgerHasPostings === true;

  let submitLabel = `Add ${count} accounts`;
  if (props.mode === "onboarding" && current && !switching) {
    submitLabel = current.dropped.length > 0 ? `Restore all ${count} accounts` : "Keep this set";
  } else if (switching || replaceable > 0) {
    submitLabel = `Replace with ${count} accounts`;
  }

  const submit = () => {
    if (!chosen) return;
    if (props.mode === "onboarding") {
      props.onChoose(chosen);
      onClose();
      return;
    }
    apply.mutate(
      { code: chosen.code, version: chosen.version, excludeCodes: [] },
      {
        onSuccess: (applied) => {
          toast({
            scheme: "success",
            title:
              applied.removed.length > 0
                ? `Chart replaced with ${chosen.name} — ${applied.removed.length} accounts removed, ${applied.added.length} added. Required and bank accounts kept.`
                : `${chosen.name} added — ${applied.added.length} accounts. Required and bank accounts kept.`,
          });
          onClose();
        },
      },
    );
  };

  return (
    <Modal.Form
      open={open}
      size="2xl"
      title={current ? "Change template" : "Add suggested accounts"}
      subtitle="Suggested accounts are ordinary accounts: you can edit them, and remove them while nothing has posted to them. Required accounts stay whatever you choose."
      submitLabel={submitLabel}
      submitDisabled={
        !chosen ||
        !detail.data ||
        (props.mode === "settings" && (!preview.data || ledgerHasPostings || blocking.length > 0))
      }
      busy={apply.isPending}
      error={refusalMessage(apply.error)}
      onClose={onClose}
      onSubmit={submit}
    >
      <RadioPresets.CardRadio
        aria-label="Template"
        orientation="horizontal"
        className="[&>div>*]:min-w-56 [&>div>*]:flex-1"
        value={chosen?.code ?? null}
        onValueChange={(details: { value: string | null }) => setPicked(details.value)}
        items={list.map((t) => ({
          value: t.code,
          label: t.name,
          description: [
            t.region === "NP" ? "Recommended for Nepal" : null,
            firstSentence(t.description).replace(/[.:]$/, ""),
            `${t.accountCount} accounts · ${regionName(t.region)}`,
          ]
            .filter(Boolean)
            .join(". "),
        }))}
      />
      <Typography.Text variant="small" muted>
        Industry templates (trading, manufacturing, services) are planned for a later release, as
        add-ons to either chart.
      </Typography.Text>

      {props.mode === "settings" && ledgerHasPostings ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "info" }}
          description="Suggested accounts can only be added before the first transaction posts. Add accounts one at a time with New account."
        />
      ) : null}

      {switching && current && chosen ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          title={`This replaces the ${kept} ${current.template.name} accounts.`}
          description={`Changing the template removes the current suggested accounts and adds ${count} accounts from ${chosen.name}. Required accounts stay.`}
        />
      ) : null}

      {props.mode === "settings" && !ledgerHasPostings && replaceable > 0 && chosen ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          title={`This replaces ${replaceable} accounts in your chart.`}
          description={`Your chart has ${replaceable} accounts besides the required and bank accounts${
            preview.data?.createdByCompany
              ? `, including ${preview.data.createdByCompany} you created`
              : ""
          }. Applying ${chosen.name} removes all of them, along with any edits, and adds its ${count} accounts. Required accounts and bank and cash accounts stay. Nothing has posted yet, so no balances are affected.`}
        />
      ) : null}

      {blocking.length > 0 ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          title="Some accounts that would be replaced are still in use"
          description={
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {blocking.map((ref) => (
                <li key={`${ref.accountId}-${ref.kind}`}>
                  {`${ref.code} · ${ref.name} — ${referenceKindName(ref.kind)} (${ref.uses})`}
                </li>
              ))}
              <li>Change or delete these first, then apply the template.</li>
            </ul>
          }
        />
      ) : null}

      {chosen ? (
        <section aria-label={`${chosen.name} preview`} className="flex min-h-0 flex-col gap-2">
          <Typography.Text weight="semibold" className="m-0">
            {`${chosen.name} · ${count} accounts`}
          </Typography.Text>
          {chosen.basis ? (
            <Typography.Text variant="small" muted className="m-0">
              {chosen.basis}
            </Typography.Text>
          ) : null}
          <div className="max-h-80 overflow-y-auto">
            {detail.data ? (
              <ClassTables
                rows={detail.data.accounts}
                columns={PREVIEW}
                label={(group) => `${group} in ${chosen.name}`}
                rowId={(row) => row.code}
              />
            ) : (
              <Typography.Text variant="small" muted>
                Loading the accounts
              </Typography.Text>
            )}
          </div>
        </section>
      ) : null}
    </Modal.Form>
  );
}
